import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { watchlistService } from '@/services/watchlist.service';
import type {
  WatchlistVideo,
  WatchlistVideoComment,
  WatchSession,
  WatchlistFilters,
  WatchSessionFilters,
  CreateWatchlistVideoInput,
  UpdateWatchlistVideoInput,
  CreateWatchSessionInput,
  UpdateWatchSessionInput,
  PaginatedWatchlistVideosResponse,
  PaginatedVideoCommentsResponse,
  PaginatedWatchSessionsResponse,
  PaginationMeta,
  RandomVideoFilters,
} from '@/types/watchlist.types';
import { ApiError } from '@/services/api';

// Video Watchlist state interface
interface VideoWatchlistState {
  videos: WatchlistVideo[];
  currentVideo: WatchlistVideo | null;
  comments: WatchlistVideoComment[];
  sessions: WatchSession[];
  currentSession: WatchSession | null;
  randomVideo: WatchlistVideo | null;
  meta: PaginationMeta | null;
  commentsMeta: PaginationMeta | null;
  sessionsMeta: PaginationMeta | null;
  filters: WatchlistFilters;
  sessionFilters: WatchSessionFilters;
  isLoading: boolean;
  isLoadingVideo: boolean;
  isLoadingComments: boolean;
  isLoadingSessions: boolean;
  isLoadingSession: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isRating: boolean;
  isAddingComment: boolean;
  isCreatingSession: boolean;
  isJoiningSession: boolean;
  isLoadingRandom: boolean;
  isMarkingWatched: boolean;
  error: string | null;
}

// Initial state
const initialState: VideoWatchlistState = {
  videos: [],
  currentVideo: null,
  comments: [],
  sessions: [],
  currentSession: null,
  randomVideo: null,
  meta: null,
  commentsMeta: null,
  sessionsMeta: null,
  filters: {
    page: 1,
    limit: 12,
    sortBy: 'date',
    sortOrder: 'desc',
  },
  sessionFilters: {
    page: 1,
    limit: 10,
    upcoming: true,
  },
  isLoading: false,
  isLoadingVideo: false,
  isLoadingComments: false,
  isLoadingSessions: false,
  isLoadingSession: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isRating: false,
  isAddingComment: false,
  isCreatingSession: false,
  isJoiningSession: false,
  isLoadingRandom: false,
  isMarkingWatched: false,
  error: null,
};

// ==============================================
// Async Thunks - Video Operations
// ==============================================

export const fetchVideos = createAsyncThunk<
  PaginatedWatchlistVideosResponse,
  WatchlistFilters | undefined,
  { rejectValue: string }
>('videoWatchlist/fetchVideos', async (filters, { rejectWithValue, getState }) => {
  try {
    const state = getState() as { videoWatchlist: VideoWatchlistState };
    const mergedFilters = { ...state.videoWatchlist.filters, ...filters };
    return await watchlistService.getVideos(mergedFilters);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Videos');
  }
});

export const fetchVideoById = createAsyncThunk<
  WatchlistVideo,
  string,
  { rejectValue: string }
>('videoWatchlist/fetchVideoById', async (id, { rejectWithValue }) => {
  try {
    return await watchlistService.getVideoById(id);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden des Videos');
  }
});

export const createVideo = createAsyncThunk<
  WatchlistVideo,
  CreateWatchlistVideoInput,
  { rejectValue: string }
>('videoWatchlist/createVideo', async (data, { rejectWithValue }) => {
  try {
    return await watchlistService.createVideo(data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Hinzufuegen des Videos');
  }
});

export const updateVideo = createAsyncThunk<
  WatchlistVideo,
  { id: string; data: UpdateWatchlistVideoInput },
  { rejectValue: string }
>('videoWatchlist/updateVideo', async ({ id, data }, { rejectWithValue }) => {
  try {
    return await watchlistService.updateVideo(id, data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Aktualisieren des Videos');
  }
});

export const deleteVideo = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('videoWatchlist/deleteVideo', async (id, { rejectWithValue }) => {
  try {
    await watchlistService.deleteVideo(id);
    return id;
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Loeschen des Videos');
  }
});

// ==============================================
// Async Thunks - Rating Operations
// ==============================================

export const rateVideo = createAsyncThunk<
  WatchlistVideo,
  { id: string; rating: number },
  { rejectValue: string }
>('videoWatchlist/rateVideo', async ({ id, rating }, { rejectWithValue }) => {
  try {
    return await watchlistService.rateVideo(id, { rating });
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Bewerten des Videos');
  }
});

export const removeRating = createAsyncThunk<
  WatchlistVideo,
  string,
  { rejectValue: string }
>('videoWatchlist/removeRating', async (id, { rejectWithValue }) => {
  try {
    return await watchlistService.removeRating(id);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Entfernen der Bewertung');
  }
});

// ==============================================
// Async Thunks - Comment Operations
// ==============================================

export const fetchVideoComments = createAsyncThunk<
  PaginatedVideoCommentsResponse,
  { videoId: string; page?: number; limit?: number },
  { rejectValue: string }
>('videoWatchlist/fetchComments', async ({ videoId, page = 1, limit = 20 }, { rejectWithValue }) => {
  try {
    return await watchlistService.getComments(videoId, page, limit);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Kommentare');
  }
});

export const addVideoComment = createAsyncThunk<
  WatchlistVideoComment,
  { videoId: string; comment: string },
  { rejectValue: string }
>('videoWatchlist/addComment', async ({ videoId, comment }, { rejectWithValue }) => {
  try {
    return await watchlistService.addComment(videoId, { comment });
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Hinzufuegen des Kommentars');
  }
});

export const deleteVideoComment = createAsyncThunk<
  { videoId: string; commentId: string },
  { videoId: string; commentId: string },
  { rejectValue: string }
>('videoWatchlist/deleteComment', async ({ videoId, commentId }, { rejectWithValue }) => {
  try {
    await watchlistService.deleteComment(videoId, commentId);
    return { videoId, commentId };
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Loeschen des Kommentars');
  }
});

// ==============================================
// Async Thunks - Watch Session Operations
// ==============================================

export const fetchWatchSessions = createAsyncThunk<
  PaginatedWatchSessionsResponse,
  WatchSessionFilters | undefined,
  { rejectValue: string }
>('videoWatchlist/fetchSessions', async (filters, { rejectWithValue, getState }) => {
  try {
    const state = getState() as { videoWatchlist: VideoWatchlistState };
    const mergedFilters = { ...state.videoWatchlist.sessionFilters, ...filters };
    return await watchlistService.getWatchSessions(mergedFilters);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Watch Sessions');
  }
});

export const fetchWatchSessionById = createAsyncThunk<
  WatchSession,
  string,
  { rejectValue: string }
>('videoWatchlist/fetchSessionById', async (sessionId, { rejectWithValue }) => {
  try {
    return await watchlistService.getWatchSessionById(sessionId);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Watch Session');
  }
});

export const createWatchSession = createAsyncThunk<
  WatchSession,
  { videoId: string; data: CreateWatchSessionInput },
  { rejectValue: string }
>('videoWatchlist/createSession', async ({ videoId, data }, { rejectWithValue }) => {
  try {
    return await watchlistService.createWatchSession(videoId, data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Erstellen der Watch Session');
  }
});

export const updateWatchSession = createAsyncThunk<
  WatchSession,
  { sessionId: string; data: UpdateWatchSessionInput },
  { rejectValue: string }
>('videoWatchlist/updateSession', async ({ sessionId, data }, { rejectWithValue }) => {
  try {
    return await watchlistService.updateWatchSession(sessionId, data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Aktualisieren der Watch Session');
  }
});

export const joinWatchSession = createAsyncThunk<
  WatchSession,
  string,
  { rejectValue: string }
>('videoWatchlist/joinSession', async (sessionId, { rejectWithValue }) => {
  try {
    return await watchlistService.joinWatchSession(sessionId);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Beitreten der Watch Session');
  }
});

export const leaveWatchSession = createAsyncThunk<
  WatchSession,
  string,
  { rejectValue: string }
>('videoWatchlist/leaveSession', async (sessionId, { rejectWithValue }) => {
  try {
    return await watchlistService.leaveWatchSession(sessionId);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Verlassen der Watch Session');
  }
});

// ==============================================
// Async Thunks - Watch History Operations
// ==============================================

export const markAsWatched = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('videoWatchlist/markAsWatched', async (videoId, { rejectWithValue }) => {
  try {
    await watchlistService.markAsWatched(videoId);
    return videoId;
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Markieren als gesehen');
  }
});

export const unmarkAsWatched = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('videoWatchlist/unmarkAsWatched', async (videoId, { rejectWithValue }) => {
  try {
    await watchlistService.unmarkAsWatched(videoId);
    return videoId;
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Entfernen der Markierung');
  }
});

export const fetchRandomVideo = createAsyncThunk<
  WatchlistVideo,
  RandomVideoFilters | undefined,
  { rejectValue: string }
>('videoWatchlist/fetchRandomVideo', async (filters, { rejectWithValue }) => {
  try {
    return await watchlistService.getRandomVideo(filters);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden des zufälligen Videos');
  }
});

// ==============================================
// Slice
// ==============================================

const videoWatchlistSlice = createSlice({
  name: 'videoWatchlist',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentVideo: (state) => {
      state.currentVideo = null;
      state.comments = [];
      state.commentsMeta = null;
    },
    clearCurrentSession: (state) => {
      state.currentSession = null;
    },
    setFilters: (state, action: PayloadAction<Partial<WatchlistFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    setSessionFilters: (state, action: PayloadAction<Partial<WatchSessionFilters>>) => {
      state.sessionFilters = { ...state.sessionFilters, ...action.payload };
    },
    resetSessionFilters: (state) => {
      state.sessionFilters = initialState.sessionFilters;
    },
    // For real-time updates via WebSocket
    updateSessionPlayback: (state, action: PayloadAction<{ sessionId: string; currentTime: number; isPlaying: boolean }>) => {
      if (state.currentSession?.id === action.payload.sessionId) {
        state.currentSession.currentTime = action.payload.currentTime;
        state.currentSession.isPlaying = action.payload.isPlaying;
      }
      const sessionIndex = state.sessions.findIndex(s => s.id === action.payload.sessionId);
      if (sessionIndex !== -1) {
        state.sessions[sessionIndex].currentTime = action.payload.currentTime;
        state.sessions[sessionIndex].isPlaying = action.payload.isPlaying;
      }
    },
    addSessionParticipant: (state, action: PayloadAction<{ sessionId: string; participant: WatchSession['participants'][0] }>) => {
      if (state.currentSession?.id === action.payload.sessionId) {
        state.currentSession.participants.push(action.payload.participant);
        state.currentSession._count.participants++;
      }
    },
    removeSessionParticipant: (state, action: PayloadAction<{ sessionId: string; userId: string }>) => {
      if (state.currentSession?.id === action.payload.sessionId) {
        state.currentSession.participants = state.currentSession.participants.filter(
          p => p.userId !== action.payload.userId
        );
        state.currentSession._count.participants--;
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch videos
    builder
      .addCase(fetchVideos.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVideos.fulfilled, (state, action) => {
        state.isLoading = false;
        state.videos = action.payload.videos;
        state.meta = action.payload.meta;
      })
      .addCase(fetchVideos.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });

    // Fetch single video
    builder
      .addCase(fetchVideoById.pending, (state) => {
        state.isLoadingVideo = true;
        state.error = null;
      })
      .addCase(fetchVideoById.fulfilled, (state, action) => {
        state.isLoadingVideo = false;
        state.currentVideo = action.payload;
      })
      .addCase(fetchVideoById.rejected, (state, action) => {
        state.isLoadingVideo = false;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });

    // Create video
    builder
      .addCase(createVideo.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createVideo.fulfilled, (state, action) => {
        state.isCreating = false;
        state.videos.unshift(action.payload);
        if (state.meta) {
          state.meta.total++;
        }
      })
      .addCase(createVideo.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });

    // Update video
    builder
      .addCase(updateVideo.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateVideo.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.videos.findIndex(v => v.id === action.payload.id);
        if (index !== -1) {
          state.videos[index] = action.payload;
        }
        if (state.currentVideo?.id === action.payload.id) {
          state.currentVideo = action.payload;
        }
      })
      .addCase(updateVideo.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });

    // Delete video
    builder
      .addCase(deleteVideo.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteVideo.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.videos = state.videos.filter(v => v.id !== action.payload);
        if (state.currentVideo?.id === action.payload) {
          state.currentVideo = null;
        }
        if (state.meta) {
          state.meta.total--;
        }
      })
      .addCase(deleteVideo.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });

    // Rate video
    builder
      .addCase(rateVideo.pending, (state) => {
        state.isRating = true;
        state.error = null;
      })
      .addCase(rateVideo.fulfilled, (state, action) => {
        state.isRating = false;
        const index = state.videos.findIndex(v => v.id === action.payload.id);
        if (index !== -1) {
          state.videos[index] = action.payload;
        }
        if (state.currentVideo?.id === action.payload.id) {
          state.currentVideo = action.payload;
        }
      })
      .addCase(rateVideo.rejected, (state, action) => {
        state.isRating = false;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });

    // Remove rating
    builder
      .addCase(removeRating.pending, (state) => {
        state.isRating = true;
        state.error = null;
      })
      .addCase(removeRating.fulfilled, (state, action) => {
        state.isRating = false;
        const index = state.videos.findIndex(v => v.id === action.payload.id);
        if (index !== -1) {
          state.videos[index] = action.payload;
        }
        if (state.currentVideo?.id === action.payload.id) {
          state.currentVideo = action.payload;
        }
      })
      .addCase(removeRating.rejected, (state, action) => {
        state.isRating = false;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });

    // Fetch comments
    builder
      .addCase(fetchVideoComments.pending, (state) => {
        state.isLoadingComments = true;
        state.error = null;
      })
      .addCase(fetchVideoComments.fulfilled, (state, action) => {
        state.isLoadingComments = false;
        state.comments = action.payload.comments;
        state.commentsMeta = action.payload.meta;
      })
      .addCase(fetchVideoComments.rejected, (state, action) => {
        state.isLoadingComments = false;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });

    // Add comment
    builder
      .addCase(addVideoComment.pending, (state) => {
        state.isAddingComment = true;
        state.error = null;
      })
      .addCase(addVideoComment.fulfilled, (state, action) => {
        state.isAddingComment = false;
        state.comments.unshift(action.payload);
        if (state.commentsMeta) {
          state.commentsMeta.total++;
        }
        if (state.currentVideo) {
          state.currentVideo._count.comments++;
        }
      })
      .addCase(addVideoComment.rejected, (state, action) => {
        state.isAddingComment = false;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });

    // Delete comment
    builder
      .addCase(deleteVideoComment.fulfilled, (state, action) => {
        state.comments = state.comments.filter(c => c.id !== action.payload.commentId);
        if (state.commentsMeta) {
          state.commentsMeta.total--;
        }
        if (state.currentVideo) {
          state.currentVideo._count.comments--;
        }
      });

    // Fetch watch sessions
    builder
      .addCase(fetchWatchSessions.pending, (state) => {
        state.isLoadingSessions = true;
        state.error = null;
      })
      .addCase(fetchWatchSessions.fulfilled, (state, action) => {
        state.isLoadingSessions = false;
        state.sessions = action.payload.sessions;
        state.sessionsMeta = action.payload.meta;
      })
      .addCase(fetchWatchSessions.rejected, (state, action) => {
        state.isLoadingSessions = false;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });

    // Fetch single session
    builder
      .addCase(fetchWatchSessionById.pending, (state) => {
        state.isLoadingSession = true;
        state.error = null;
      })
      .addCase(fetchWatchSessionById.fulfilled, (state, action) => {
        state.isLoadingSession = false;
        state.currentSession = action.payload;
      })
      .addCase(fetchWatchSessionById.rejected, (state, action) => {
        state.isLoadingSession = false;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });

    // Create session
    builder
      .addCase(createWatchSession.pending, (state) => {
        state.isCreatingSession = true;
        state.error = null;
      })
      .addCase(createWatchSession.fulfilled, (state, action) => {
        state.isCreatingSession = false;
        state.sessions.unshift(action.payload);
        state.currentSession = action.payload;
      })
      .addCase(createWatchSession.rejected, (state, action) => {
        state.isCreatingSession = false;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });

    // Update session
    builder
      .addCase(updateWatchSession.fulfilled, (state, action) => {
        const index = state.sessions.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.sessions[index] = action.payload;
        }
        if (state.currentSession?.id === action.payload.id) {
          state.currentSession = action.payload;
        }
      });

    // Join session
    builder
      .addCase(joinWatchSession.pending, (state) => {
        state.isJoiningSession = true;
        state.error = null;
      })
      .addCase(joinWatchSession.fulfilled, (state, action) => {
        state.isJoiningSession = false;
        const index = state.sessions.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.sessions[index] = action.payload;
        }
        if (state.currentSession?.id === action.payload.id) {
          state.currentSession = action.payload;
        }
      })
      .addCase(joinWatchSession.rejected, (state, action) => {
        state.isJoiningSession = false;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });

    // Leave session
    builder
      .addCase(leaveWatchSession.fulfilled, (state, action) => {
        const index = state.sessions.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.sessions[index] = action.payload;
        }
        if (state.currentSession?.id === action.payload.id) {
          state.currentSession = action.payload;
        }
      });

    // Mark as watched
    builder
      .addCase(markAsWatched.pending, (state) => {
        state.isMarkingWatched = true;
        state.error = null;
      })
      .addCase(markAsWatched.fulfilled, (state, action) => {
        state.isMarkingWatched = false;
        // Update hasWatched flag for the video in the list
        const video = state.videos.find(v => v.id === action.payload);
        if (video) {
          video.hasWatched = true;
        }
        // Update current video if it matches
        if (state.currentVideo?.id === action.payload) {
          state.currentVideo.hasWatched = true;
        }
      })
      .addCase(markAsWatched.rejected, (state, action) => {
        state.isMarkingWatched = false;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });

    // Unmark as watched
    builder
      .addCase(unmarkAsWatched.pending, (state) => {
        state.isMarkingWatched = true;
        state.error = null;
      })
      .addCase(unmarkAsWatched.fulfilled, (state, action) => {
        state.isMarkingWatched = false;
        // Update hasWatched flag for the video in the list
        const video = state.videos.find(v => v.id === action.payload);
        if (video) {
          video.hasWatched = false;
        }
        // Update current video if it matches
        if (state.currentVideo?.id === action.payload) {
          state.currentVideo.hasWatched = false;
        }
      })
      .addCase(unmarkAsWatched.rejected, (state, action) => {
        state.isMarkingWatched = false;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });

    // Fetch random video
    builder
      .addCase(fetchRandomVideo.pending, (state) => {
        state.isLoadingRandom = true;
        state.error = null;
      })
      .addCase(fetchRandomVideo.fulfilled, (state, action) => {
        state.isLoadingRandom = false;
        state.randomVideo = action.payload;
      })
      .addCase(fetchRandomVideo.rejected, (state, action) => {
        state.isLoadingRandom = false;
        state.randomVideo = null;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });
  },
});

// Export actions
export const {
  clearError,
  clearCurrentVideo,
  clearCurrentSession,
  setFilters,
  resetFilters,
  setSessionFilters,
  resetSessionFilters,
  updateSessionPlayback,
  addSessionParticipant,
  removeSessionParticipant,
} = videoWatchlistSlice.actions;

// Export selectors
export const selectVideos = (state: { videoWatchlist: VideoWatchlistState }) => state.videoWatchlist.videos;
export const selectCurrentVideo = (state: { videoWatchlist: VideoWatchlistState }) => state.videoWatchlist.currentVideo;
export const selectRandomVideo = (state: { videoWatchlist: VideoWatchlistState }) => state.videoWatchlist.randomVideo;
export const selectComments = (state: { videoWatchlist: VideoWatchlistState }) => state.videoWatchlist.comments;
export const selectSessions = (state: { videoWatchlist: VideoWatchlistState }) => state.videoWatchlist.sessions;
export const selectCurrentSession = (state: { videoWatchlist: VideoWatchlistState }) => state.videoWatchlist.currentSession;
export const selectMeta = (state: { videoWatchlist: VideoWatchlistState }) => state.videoWatchlist.meta;
export const selectCommentsMeta = (state: { videoWatchlist: VideoWatchlistState }) => state.videoWatchlist.commentsMeta;
export const selectSessionsMeta = (state: { videoWatchlist: VideoWatchlistState }) => state.videoWatchlist.sessionsMeta;
export const selectFilters = (state: { videoWatchlist: VideoWatchlistState }) => state.videoWatchlist.filters;
export const selectSessionFilters = (state: { videoWatchlist: VideoWatchlistState }) => state.videoWatchlist.sessionFilters;
export const selectVideoWatchlistLoading = (state: { videoWatchlist: VideoWatchlistState }) => ({
  isLoading: state.videoWatchlist.isLoading,
  isLoadingVideo: state.videoWatchlist.isLoadingVideo,
  isLoadingComments: state.videoWatchlist.isLoadingComments,
  isLoadingSessions: state.videoWatchlist.isLoadingSessions,
  isLoadingSession: state.videoWatchlist.isLoadingSession,
  isCreating: state.videoWatchlist.isCreating,
  isUpdating: state.videoWatchlist.isUpdating,
  isDeleting: state.videoWatchlist.isDeleting,
  isRating: state.videoWatchlist.isRating,
  isAddingComment: state.videoWatchlist.isAddingComment,
  isCreatingSession: state.videoWatchlist.isCreatingSession,
  isJoiningSession: state.videoWatchlist.isJoiningSession,
  isLoadingRandom: state.videoWatchlist.isLoadingRandom,
  isMarkingWatched: state.videoWatchlist.isMarkingWatched,
});
export const selectVideoWatchlistError = (state: { videoWatchlist: VideoWatchlistState }) => state.videoWatchlist.error;

export default videoWatchlistSlice.reducer;
